import React, { memo, useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import isEqual from 'lodash/isEqual';

import { withExperienceTracker } from '@confluence/experience-tracker';
import { NewCommentsContext } from '@confluence/comment-context';

import { CommentBatch } from './CommentBatch';
import { CommentsContainer } from './CommentsContainer';

export const BATCH_SIZE = 10;

export const CommentsListBatchComponent = ({
	contentType,
	editor,
	canViewProfile,
	contentId,
	editorSaveHandler,
	editorCancelHandler,
	comments,
	commentActions,
	showAuthorLabel,
	onEditorLoaded,
	onEditorContentChanged,
	spaceId,
	canReplyToComment,
	experienceTracker,
	useNewExperienceAbort,
	onRendered,
	userInfo,
	highlightedComments,
	focusOnCommentId,
	autoFocusedCommentId,
	onCommentAutoFocused,
}) => {
	const batchCount = Math.ceil(comments.length / BATCH_SIZE);
	const batches = useMemo(() => new Array(batchCount).fill(0), [batchCount]);
	const [batch, setBatch] = useState(0);

	const onBatchRendered = useCallback((batchIndex) => {
		setBatch(batchIndex + 1);
	}, []);

	const genericProps = {
		contentId,
		contentType,
		commentActions,
		canViewProfile,
		showAuthorLabel,
		editor,
		editorSaveHandler,
		editorCancelHandler,
		onEditorLoaded,
		onEditorContentChanged,
		spaceId,
		canReplyToComment,
		useNewExperienceAbort,
		experienceTracker,
		onRendered,
	};

	return (
		<CommentsContainer
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
			className="comment-threads top-level"
			id="page-comments"
			data-test-id="page-comments"
		>
			<NewCommentsContext.Consumer>
				{({ newCommentPositions }) => {
					const newComments =
						newCommentPositions?.map((commentPosition) => commentPosition.id) || [];

					return batches.map((_, index) => (
						<CommentBatch
							genericProps={genericProps}
							key={`${contentId}-batch-${index}`}
							canRenderBatch={index <= batch}
							batchSize={BATCH_SIZE}
							batchIndex={index}
							comments={comments}
							userInfo={userInfo}
							focusOnCommentId={focusOnCommentId}
							highlightedComments={highlightedComments}
							newComments={newComments}
							autoFocusedCommentId={autoFocusedCommentId}
							onCommentAutoFocused={onCommentAutoFocused}
							onBatchRendered={onBatchRendered}
						/>
					));
				}}
			</NewCommentsContext.Consumer>
		</CommentsContainer>
	);
};

export const CommentsListBatch = memo(
	withExperienceTracker(CommentsListBatchComponent),
	(prevProps, nextProps) => isEqual(prevProps, nextProps),
);

CommentsListBatchComponent.propTypes = {
	/**
	 * The content id that the comment belongs to
	 */
	contentId: PropTypes.string.isRequired,
	/**
	 * Content Type of the page
	 */
	contentType: PropTypes.oneOf(['blogpost', 'page']),
	/**
	 * current user information
	 */
	userInfo: PropTypes.object,
	/**
	 * Can current user view profiles of not
	 */
	canViewProfile: PropTypes.bool,
	/**
	 * Total number of comments
	 */
	focusOnCommentId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
	/**
	 * Array of comments (Immutable List)
	 */
	comments: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
	/**
	 * Array of comments ids that should be highlighted
	 */
	highlightedComments: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
	/**
	 * Should we show `AUTHOR` label or not
	 */
	showAuthorLabel: PropTypes.bool,
	/**
	 * Editor properties
	 */
	/**
	 * Custom editor save handler
	 */
	editorSaveHandler: PropTypes.func,
	/**
	 * Custom editor cancel handler
	 */
	editorCancelHandler: PropTypes.func,
	/**
	 * Object with actions for comment
	 */
	commentActions: PropTypes.object,
	/**
	 * Callback function when editor has been loaded
	 */
	onEditorLoaded: PropTypes.func,
	/**
	 * Callback function when editor has content changes.
	 * Used to handle unsaved changes warning messages.
	 */
	onEditorContentChanged: PropTypes.func,
	/**
	 * The editor state object
	 */
	editor: PropTypes.object,
	/**
	 * SpaceId of current space
	 */
	spaceId: PropTypes.string.isRequired,
	/**
	 * Add/Reply comment Permission
	 */
	canReplyToComment: PropTypes.bool.isRequired,
	/**
	 * Id of comment that should receive focus
	 */
	autoFocusedCommentId: PropTypes.string,
	/**
	 * Used for resetting autoFocusedCommentId
	 */
	onCommentAutoFocused: PropTypes.func,
	/**
	 * Injected ExperienceTracker object
	 */
	experienceTracker: PropTypes.object,
	/**
	 * A feature flag to control aborting add-page-comment more aggressively to see how it affects SLA
	 */
	useNewExperienceAbort: PropTypes.bool,
	onRendered: PropTypes.func,
};

CommentsListBatch.defaultProps = {
	highlightedComments: [],
	onEditorLoaded: () => {},
};
